/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Button,
    Wrap,
    WrapItem,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CustomCloseWhite} from '../../../components/icons';
import {REMOVE_FILTER} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const SelectedRefinements = ({toggleFilter, selectedFilterValues, filters, handleReset, ...props}) => {
    const styles = useMultiStyleConfig('PageFilters')
    const {formatMessage, formatNumber} = useIntl()
    const {currency} = useCurrency()

    let selectedFilters = []
    for (const key in selectedFilterValues) {
        const filters = selectedFilterValues[key].split('|')
        filters?.forEach((filter) => {
            let uiLabel = filter

            if (key === 'price') {
                // Parse price range string like (1..41)
                const match = filter.match(/\((\d+(?:\.\d+)?)\.\.(\d+(?:\.\d+)?)\)/)
                if (match) {
                    const min = parseFloat(match[1])
                    const max = parseFloat(match[2])

                    // Format min and max as currency without decimals
                    const formattedMin = formatNumber(min, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0
                    })
                    const formattedMax = formatNumber(max, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0
                    })

                    uiLabel = `${formattedMin} - ${formattedMax}`
                }
            }

            const selected = {
                uiLabel,
                value: key,
                apiLabel: filter
            }

            if (selected.value !== 'htype' && selected.value !== 'cgid') {
                selectedFilters.push(selected)
            }
        })
    }

    if (selectedFilters?.length === 0) {
        return null
    }

    return (
        <Wrap
            direction="row"
            align="center"
            display="flex"
            flexWrap="wrap"
            data-testid="sf-selected-refinements"
            spacing={props?.spacing || 1}
            {...styles.srContainer}
        >
            {selectedFilters?.map((filter, idx) => {
                return (
                    <WrapItem key={idx}>
                        <Box marginLeft={0}>
                            <Button
                                variant={'selectedFilter'}
                                iconSpacing={1}
                                rightIcon={
                                    <CustomCloseWhite {...styles.srRemove}/>
                                }
                                onClick={() =>
                                    toggleFilter({value: filter.apiLabel}, filter.value, true)
                                }
                                aria-label={formatMessage(REMOVE_FILTER, {label: filter.uiLabel})}
                            >
                                <Text as="span" {...styles.filterLabel}>
                                    {filter.uiLabel}
                                </Text>
                            </Button>
                        </Box>
                    </WrapItem>
                )
            })}

            {selectedFilters?.length > 0 && (
                <WrapItem {...styles.srClearAllWrapper}>
                    <Box>
                        <Button
                            padding={{base: 2}}
                            variant="unstyled"
                            onClick={handleReset}
                            aria-label={formatMessage({
                                id: 'selected_refinements.action.assistive_msg.clear_all',
                                defaultMessage: 'Clear all filters'
                            })}
                            {...styles.srClearAllCTA}
                        >
                            <Box as={'span'} {...styles.srClearAllCTA}>
                                <FormattedMessage
                                    defaultMessage="Clear All"
                                    id="selected_refinements.action.clear_all"
                                />
                            </Box>
                        </Button>
                    </Box>
                </WrapItem>
            )}
        </Wrap>
    )
}

SelectedRefinements.propTypes = {
    filters: PropTypes.array,
    selectedFilterValues: PropTypes.object,
    toggleFilter: PropTypes.func,
    handleReset: PropTypes.func
}

export default SelectedRefinements
